import { z } from 'zod';
import { watchAlertPayloadSchema } from './watch-alerts';
const looseEventSchema = z.unknown();
export const queueRequestKindSchema = z.enum(['standard', 'inbound_hearing']);
export const queueMessageSchema = z.object({
    clientId: z.string().optional(),
    data: z.unknown().optional(),
    name: z.string().optional(),
    /**
     * auth_success to an interpreter or captioner, and public_number_changed
     * when an admin changes it: the public number the people they serve see,
     * "Interpreter #1042" (shared/provider-label.js).
     */
    publicNumber: z.number().int().positive().nullable().optional(),
    role: z.string().optional(),
    token: z.string().optional(),
    type: z.string(),
    userId: z.string().optional(),
    deviceId: z.string().optional()
}).passthrough();
export const interpreterInfoSchema = z.object({
    id: z.string(),
    languages: z.array(z.string()),
    name: z.string(),
    status: z.enum(['active', 'busy', 'inactive'])
}).passthrough();
/**
 * The client's own Zoom/Meet/Teams/Webex meeting ("Interpret my meeting",
 * vrs-server/lib/external-meeting-vrs.js). Offers and queue views carry
 * platform + host; url (it can hold a passcode) only reaches the interpreter
 * and client of the committed call.
 */
export const externalMeetingSchema = z.object({
    host: z.string().optional(),
    platform: z.enum(['zoom', 'meet', 'teams', 'webex']).nullable().optional(),
    url: z.string().optional()
}).passthrough();
export const requestInfoSchema = z.object({
    callerIdPhone: z.string().optional().nullable(),
    callType: z.enum(['vrs', 'vri']).optional(),
    corporateAccountId: z.string().optional().nullable(),
    costCenter: z.string().optional().nullable(),
    department: z.string().optional().nullable(),
    industryTag: z.string().optional().nullable(),
    instructions: z.string().optional().nullable(),
    clientName: z.string(),
    id: z.string(),
    language: z.string(),
    position: z.number().optional(),
    requestKind: queueRequestKindSchema.optional(),
    roomName: z.string().optional(),
    scheduledSessionId: z.string().optional().nullable(),
    scheduledStartAt: z.string().optional().nullable(),
    targetPhone: z.string().optional().nullable(),
    targetPhoneLast4: z.string().regex(/^\d{4}$/).optional(),
    timestamp: z.number().optional(),
    vcoEnabled: z.boolean().optional(),
    externalMeeting: externalMeetingSchema.optional(),
    /** Server-owned: the client is a Demo account, so the call is not payable. */
    demoMode: z.boolean().optional(),
    /**
     * On a client's own queue_status entries only: whether the receiving
     * browser placed this request (a reloaded portal takes it back). Absent
     * when the server does not know which browser asked.
     */
    forThisDevice: z.boolean().optional()
}).passthrough();
export const queueMatchPayloadSchema = z.object({
    callerIdPhone: z.string().optional().nullable(),
    callId: z.string().optional(),
    callType: z.enum(['vrs', 'vri']).optional(),
    clientId: z.string().optional(),
    clientName: z.string().optional(),
    clientStartWithVideoMuted: z.boolean().optional(),
    durationMinutes: z.number().optional(),
    endedBy: z.string().optional(),
    // The person the interpreter is tied to (docs/interpreted-group-calls.md):
    // on the interpreter's meeting_initiated / request_accepted.
    anchorId: z.string().optional(),
    anchorName: z.string().optional(),
    // The interpreter's copy of call_ended after the anchor's connection
    // dropped offers a call-back; meeting_initiated for a call-back room says
    // which call it calls back.
    callback: z.object({
        available: z.boolean().optional(),
        anchorName: z.string().nullable().optional(),
        rosterCount: z.number().optional(),
        expiresAt: z.string().optional(),
        parentCallId: z.string().optional(),
        attempt: z.number().optional()
    }).passthrough().optional(),
    // Display name of endedBy when the host ended a group call for everyone
    // (reason 'ended_for_all'): "Dana ended the call for everyone."
    endedByName: z.string().optional(),
    // Set on the call_ended a group participant receives for their own
    // departure: the call goes on for everyone else.
    participantId: z.string().optional(),
    endedByRole: z.string().optional(),
    endReason: z.string().optional(),
    interpreterId: z.string().optional(),
    interpreterName: z.string().optional(),
    language: z.string().optional(),
    reason: z.string().optional(),
    requestKind: queueRequestKindSchema.optional(),
    requestId: z.string().optional(),
    roomName: z.string().optional(),
    // Which mechanism ended the call: 'participant_call_end', or the guard's
    // own name ('client_no_show_guard', 'stale_call_sweeper', …). endedByRole
    // alone says 'system' for six different terminators, which is what made
    // the 2026-08-15 teardown unattributable without server log access.
    surface: z.string().optional(),
    targetPhone: z.string().optional().nullable(),
    targetPhoneLast4: z.string().regex(/^\d{4}$/).optional(),
    vcoEnabled: z.boolean().optional(),
    externalMeeting: externalMeetingSchema.optional(),
    /**
     * On the match of an interpreter who took a VRS call by transfer: the
     * hearing party's live phone leg, to join rather than dial
     * (vrs-server/lib/voice-leg-handover.js).
     */
    phoneLeg: z.object({
        callSid: z.string().min(1),
        conferenceName: z.string().min(1),
        sessionId: z.string().optional().nullable()
    }).optional(),
    /** The transfer that brought an interpreter in (their match), or took them out (their call_ended). */
    transferId: z.string().optional(),
    /** On the sender's call_ended { reason: 'call_transferred' }: who has the call now. */
    transferredToName: z.string().optional().nullable(),
    /**
     * On the same call_ended: whether the phone leg went to them. Only then
     * does the sender's device let it go without hanging up.
     */
    phoneLegMoved: z.boolean().optional(),
    /** Epoch ms (server clock) the client's request was created (match messages). */
    requestCreatedAt: z.number().optional(),
    /** Epoch ms (server clock) the interpreter's accept was committed (match messages). */
    matchedAt: z.number().optional(),
    /**
     * This recipient's own Jitsi room token, signed at Accept with the rules of
     * POST /api/auth/jitsi-token. Absent for inbound hearing and external
     * meetings, when signing failed, or when the client's socket was on another
     * instance: mint one then. Never stored or logged.
     */
    roomToken: z.string().optional(),
    /**
     * On the client's queue match (match_found/meeting_initiated): true only
     * on the copy sent to the browser that placed the request, which enters
     * the room even if it lost its own record of the request; false on any
     * other browser of the account, which rings. Absent when the server does
     * not know which browser asked, on fan-out through another instance, and
     * on re-rings.
     */
    forThisDevice: z.boolean().optional(),
    /**
     * On a client's match that rings them while no device of theirs has
     * answered (an interpreter's Call client, the Ring of an inbound hearing
     * call): the ring ledger's ring (kind interpreter_ready), with the id its
     * push carried as callUUID and when it stops (epoch ms). A browser that
     * did not place a request rings with it; the first device to join the
     * room answers (vrs-server/ws/answer-at-join.js), and ring_ended names
     * the same ringId. Absent on a match a device asked for or holds.
     */
    ringId: z.string().optional(),
    callUUID: z.string().optional(),
    ringDeadline: z.number().optional()
}).passthrough();
export const interpreterRequestPayloadSchema = z.object({
    callerIdPhone: z.string().optional().nullable(),
    callType: z.enum(['vrs', 'vri']).optional(),
    corporateAccountId: z.string().optional().nullable(),
    costCenter: z.string().optional().nullable(),
    department: z.string().optional().nullable(),
    industryTag: z.string().optional().nullable(),
    instructions: z.string().optional().nullable(),
    clientName: z.string(),
    id: z.string(),
    language: z.string(),
    /** UUID of the current durable offer generation; absent on older servers/P2P. */
    offerId: z.string().uuid().optional(),
    /** ISO time the queue withdraws this offer (server QUEUE_OFFER_WINDOW_MS). */
    offerExpiresAt: z.string().optional(),
    /** Epoch ms (server clock) this offer generation was made; redeliveries keep it. */
    offeredAt: z.number().optional(),
    requestKind: queueRequestKindSchema.optional(),
    roomName: z.string().optional(),
    scheduledSessionId: z.string().optional().nullable(),
    scheduledStartAt: z.string().optional().nullable(),
    targetPhone: z.string().optional().nullable(),
    targetPhoneLast4: z.string().regex(/^\d{4}$/).optional(),
    timestamp: z.number().optional(),
    vcoEnabled: z.boolean().optional(),
    externalMeeting: externalMeetingSchema.optional(),
    /** Server-owned: the client is a Demo account, so the call is not payable. */
    demoMode: z.boolean().optional()
}).passthrough();
export const queueErrorPayloadSchema = z.object({
    /**
     * What was refused, e.g. 'accept_request', 'prepare_vri_invite', or
     * 'request_interpreter' (every refusal of the request itself, including a
     * payload validation_error): only that one ends a waiting request.
     */
    operation: z.string().optional(),
    requestId: z.string().optional(),
    roomName: z.string().optional(),
    code: z.string().optional(),
    message: z.string().optional(),
    retrying: z.boolean().optional(),
    // conference_invite_cancel refusals name the ring they were about.
    targetId: z.string().nullable().optional(),
    // prepare_vri_invite's PRIVATE_DEMO_CALL: whether the private demo pair's
    // call still takes Demo accounts (KUWAIT_DEMO_PAIR_INVITES).
    demoAccountsOnly: z.boolean().optional()
}).passthrough();
/*
 * An interpreter calling a client, rather than waiting for one to arrive.
 *
 * The mirror of requestInterpreter. Room entry is not carried by these: the
 * server follows the started event with `meeting_initiated`, so the ordinary
 * match path takes the interpreter into the call. These two say what to show
 * while that happens, and what to say when the client cannot be reached —
 * busy, do-not-disturb and unreachable all fail closed server-side.
 */
export const interpreterClientCallStartedPayloadSchema = z.object({
    callId: z.string().optional(),
    clientName: z.string().optional(),
    roomName: z.string().optional()
}).passthrough();
export const interpreterClientCallFailedPayloadSchema = z.object({
    code: z.string().optional(),
    message: z.string().optional()
}).passthrough();
/*
 * A colleague asks this interpreter to take over their live call (warm
 * transfer, vrs-server/ws/call-management-handlers.js). The receiver answers
 * `call_transfer_accept {transferId}`, which reuses the queue-accept room
 * path (request_accepted + meeting_initiated), or `call_transfer_cancel
 * {transferId}`, acknowledged by `call_transfer_cancelled {transferId}`.
 * There is no expiry: the offer stands until one of them, until
 * `call_transfer_offer_ended` below, or until an accept is refused because
 * the call moved.
 */
export const callTransferOfferPayloadSchema = z.object({
    transferId: z.string().min(1),
    callId: z.string().optional().nullable(),
    roomName: z.string().optional().nullable(),
    callType: z.string().optional().nullable(),
    clientName: z.string().optional().nullable(),
    // The call's language (an unset one is ASL, as matching reads it).
    language: z.string().optional().nullable(),
    fromInterpreterName: z.string().optional().nullable(),
    reason: z.string().optional().nullable(),
    // When the server withdraws it unanswered (TRANSFER_OFFER_TTL_MS).
    expiresAt: z.string().optional().nullable()
}).passthrough();
/*
 * The sender's side of a transfer (docs/interpreter-transfer-handover.md).
 * call_transfer_pending: the colleague has been offered the call.
 * call_transfer_cancelled: it is off, and why; the sender stays on the call.
 * call_transfer_accepted: the colleague took it; the sender stays until the
 * server releases them with call_ended { reason: 'call_transferred' }.
 * A refusal of call_transfer is an error { code, operation: 'call_transfer',
 * callId }.
 */
export const callTransferPendingPayloadSchema = z.object({
    transferId: z.string().min(1),
    callId: z.string().min(1),
    toInterpreterId: z.string().optional().nullable(),
    toInterpreterName: z.string().optional().nullable(),
    expiresAt: z.string().optional().nullable()
}).passthrough();
export const callTransferCancelledPayloadSchema = z.object({
    transferId: z.string().min(1),
    callId: z.string().optional().nullable(),
    reason: z.enum(['declined', 'expired', 'unavailable', 'withdrawn']).optional().nullable()
}).passthrough();
export const callTransferAcceptedPayloadSchema = z.object({
    transferId: z.string().optional().nullable(),
    callId: z.string().min(1),
    roomName: z.string().optional().nullable(),
    acceptedByName: z.string().optional().nullable(),
    byAdmin: z.boolean().optional()
}).passthrough();
/*
 * Everyone in the call: who is interpreting now, once a transfer's receiver
 * is in and the sender has left (vrs-server/ws/call-transfer-handover.js).
 */
export const callInterpreterChangedPayloadSchema = z.object({
    callId: z.string().min(1),
    roomName: z.string().optional().nullable(),
    transferId: z.string().optional().nullable(),
    interpreterName: z.string().optional().nullable()
}).passthrough();
/*
 * The receiver of a transfer whose phone leg moved only on a retry
 * (vrs-server/ws/call-transfer-handover.js): join it now.
 */
export const callPhoneLegHandedOverPayloadSchema = z.object({
    callId: z.string().min(1),
    roomName: z.string().optional().nullable(),
    transferId: z.string().optional().nullable(),
    phoneLeg: z.object({
        callSid: z.string().min(1),
        conferenceName: z.string().min(1),
        sessionId: z.string().optional().nullable()
    }).passthrough()
}).passthrough();
/*
 * Both interpreters of a transfer: the phone leg stayed with the sender, who
 * stays in the call (vrs-server/ws/call-transfer-handover.js).
 */
export const callTransferPhoneLegFailedPayloadSchema = z.object({
    callId: z.string().min(1),
    roomName: z.string().optional().nullable(),
    transferId: z.string().optional().nullable()
}).passthrough();
/*
 * The receiver's offer is over (vrs-server/ws/call-transfer-offers.js): the
 * call ended ('call_ended') or its sender withdrew it ('withdrawn'). Take the
 * offer card with this transferId down.
 */
export const callTransferOfferEndedPayloadSchema = z.object({
    transferId: z.string().min(1),
    callId: z.string().optional().nullable(),
    reason: z.string().optional().nullable()
}).passthrough();
/*
 * Outside meetings (docs/outside-meeting-desk.md). A desk is the
 * interpreter's web console on their computer. It opens the client's
 * Zoom/Meet/Teams/Webex meeting. Malka Terp is the remote control.
 */
/** meeting_desk_status: the desk of this account is connected or not. */
export const meetingDeskStatusPayloadSchema = z.object({
    connected: z.boolean(),
    // The desk's device label, for example "Chrome on macOS".
    device: z.string().nullable().optional()
}).passthrough();
/**
 * external_meeting_call: the outside-meeting call of this account, after an
 * accept or at sign-in during the call. Times are ISO strings or null. This
 * schema has no url and does not pass unknown keys through: Malka Terp never
 * keeps the meeting link (it can hold a passcode).
 */
export const externalMeetingCallPayloadSchema = z.object({
    callId: z.string().min(1),
    callType: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    platform: z.string().nullable().optional(),
    host: z.string().nullable().optional(),
    enteredAt: z.string().nullable().optional(),
    identifiedAt: z.string().nullable().optional(),
    identifyBy: z.string().nullable().optional(),
    identifiedInTime: z.boolean().nullable().optional(),
    acceptedOn: z.string().nullable().optional(),
    acceptedDevice: z.string().nullable().optional(),
    deskDevice: z.string().nullable().optional()
});
/** external_meeting_status_ack: the server recorded one step of the meeting call. */
export const externalMeetingStatusAckPayloadSchema = z.object({
    callId: z.string().min(1),
    event: z.string().nullable().optional(),
    enteredAt: z.string().nullable().optional(),
    identifiedAt: z.string().nullable().optional(),
    identifyBy: z.string().nullable().optional(),
    identifiedInTime: z.boolean().nullable().optional(),
    platform: z.string().nullable().optional()
}).passthrough();
/*
 * In-call team interpreting (vrs-server/ws/interpreter-teams.js,
 * docs/interpreter-teaming.md). A colleague on a live call asks this
 * interpreter to join it as their teammate: answer `team_accept {teamId}`
 * (then `team_joined` carries the room) or `team_decline {teamId}`. The
 * offer lapses at expiresAt; `team_offer_ended` takes it down earlier.
 */
export const teamOfferPayloadSchema = z.object({
    teamId: z.string().min(1),
    callId: z.string().min(1),
    roomName: z.string().optional().nullable(),
    callType: z.string().optional().nullable(),
    language: z.string().optional().nullable(),
    clientName: z.string().optional().nullable(),
    fromInterpreterName: z.string().optional().nullable(),
    expiresAt: z.string().optional().nullable()
}).passthrough();
/*
 * A team offer is over: taken, answered_elsewhere (another device of this
 * interpreter), withdrawn, timeout, declined, ended, call_ended,
 * transferred or primary_left. Take the card with this teamId down.
 */
export const teamOfferEndedPayloadSchema = z.object({
    teamId: z.string().min(1),
    callId: z.string().optional().nullable(),
    reason: z.string().optional().nullable()
}).passthrough();
/* This interpreter won the team offer: join `roomName` as its team interpreter. */
export const teamJoinedPayloadSchema = z.object({
    teamId: z.string().min(1),
    callId: z.string().min(1),
    roomName: z.string().optional().nullable(),
    callType: z.string().optional().nullable(),
    clientName: z.string().optional().nullable(),
    primaryInterpreterId: z.string().optional().nullable(),
    primaryInterpreterName: z.string().optional().nullable(),
    membership: z.literal('team_interpreter')
}).passthrough();
/*
 * The team's state, to the interpreters in it: pending (to the one who
 * asked), accepted (the teammate joined), request ended (timeout, declined,
 * cancelled; byName for a named decline) and ended (left, released,
 * call_ended, transferred, primary_left).
 */
export const teamStatePayloadSchema = z.object({
    teamId: z.string().min(1),
    callId: z.string().optional().nullable(),
    reason: z.string().optional().nullable(),
    offered: z.number().optional(),
    toInterpreterId: z.string().optional().nullable(),
    expiresAt: z.string().optional().nullable(),
    teammateId: z.string().optional().nullable(),
    teammateName: z.string().optional().nullable(),
    byName: z.string().optional().nullable()
}).passthrough();
export const vriInvitePreparedPayloadSchema = z.object({
    requestId: z.string().optional(),
    expiresAt: z.string().optional(),
    roomName: z.string().optional(),
    status: z.enum(['prepared', 'live']).optional(),
    deliveries: z.array(z.object({
        channel: z.enum(['email', 'sms']),
        errorCode: z.string().nullable().optional(),
        status: z.enum(['sent', 'blocked', 'failed']),
        targetRef: z.string()
    }).passthrough()).optional(),
    // Who the link is for, as the server stored it (null when not given).
    guestEmail: z.string().nullable().optional(),
    guestName: z.string().nullable().optional(),
    guestPhone: z.string().nullable().optional(),
    inviteUrl: z.string().optional(),
    token: z.string().optional()
}).passthrough();
export const voicemailEventPayloadSchema = z.object({
    calleeId: z.string().optional(),
    calleeName: z.string().optional(),
    calleePhone: z.string().optional(),
    count: z.number().optional(),
    durationSeconds: z.number().optional(),
    maxDurationSeconds: z.number().optional(),
    message: z.string().optional(),
    messageId: z.string().optional(),
    roomName: z.string().optional(),
    voicemailAvailable: z.boolean().optional()
}).passthrough();
export const notificationEventPayloadSchema = z.object({
    body: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    priority: z.enum(['normal', 'high']).optional(),
    title: z.string().optional(),
    type: z.string().optional()
}).passthrough();
const vrsClientReachabilitySchema = z.object({
    calleeId: z.string().optional(),
    calleeName: z.string().optional(),
    retryAfterMs: z.number().optional(),
    voicemailAvailable: z.boolean().optional()
}).passthrough();
export const queueStatusSchema = z.object({
    activeInterpreters: z.array(interpreterInfoSchema),
    paused: z.boolean().optional(),
    pendingRequests: z.array(requestInfoSchema),
    // Interpreters only: waiting requests they would be offered once
    // Available that no other interpreter holds an offer for (eligibleQueueSize
    // in vrs-server/lib/state.js). Absent until the server knows. It starts
    // the post-call breather card's "Taking the call in 3…" countdown
    // (wrapUpActions.ts); queueSize never does.
    eligibleQueueSize: z.number().int().nonnegative().optional(),
    // Waiting requests in the recipient's tenant and service region
    // (scopedQueueCounts in vrs-server/lib/state.js): every language, service
    // mode and held offer. Interpreters get counts only.
    queueSize: z.number().int().nonnegative().optional(),
    totalMatches: z.number()
}).passthrough();
const p2pFailureSchema = z.object({
    message: z.string().optional(),
    // A group call nobody could be found for: what was entered for each.
    unresolved: z.array(z.unknown()).optional()
}).passthrough();
export const p2pIncomingSchema = queueMatchPayloadSchema.extend({
    // The ring ledger's id for this ring (= the CallKit / Telecom callUUID
    // its push carried) and when it stops (epoch ms): one ring on the phone,
    // whether it shows as this sheet or as the system ring.
    ringId: z.string().optional(),
    callUUID: z.string().optional(),
    ringDeadline: z.number().optional(),
    invitationId: z.string().optional(),
    conferenceInvitation: z.boolean().optional(),
    expiresAt: z.string().datetime().optional(),
    callerId: z.string().optional(),
    callerName: z.string().optional(),
    // The caller's photo (a signed link on the API origin) and the number
    // the person rung saved for them (vrs-server/lib/profile-photos.js).
    callerPhotoUrl: z.string().optional(),
    callerPhone: z.string().optional(),
    calleeId: z.string().optional(),
    groupCall: z.boolean().optional(),
    // Who may end a group call for everyone: the caller while they stay,
    // then the person seated longest (vrs-server summarizeCallMembership).
    hostId: z.string().optional(),
    inboundVrs: z.boolean().optional(),
    participantCount: z.number().optional()
}).passthrough();
export const RING_END_REASONS = [
    'caller_cancelled', 'answered_elsewhere', 'declined_elsewhere', 'timeout', 'ended_for_all', 'removed', 'call_ended'
];
export const ringEndedSchema = z.object({
    ringId: z.string(),
    callUUID: z.string().optional(),
    kind: z.enum(['p2p_direct', 'p2p_group', 'conference_invite', 'interpreter_ready', 'relay_inbound',
        'interpreter_offer']).optional(),
    reason: z.enum(RING_END_REASONS),
    callId: z.string().optional(),
    invitationId: z.string().optional(),
    relaySessionId: z.string().optional(),
    roomName: z.string().optional()
}).passthrough();
export const p2pTargetSchema = z.object({
    availability: z.object({
        dnd: z.number().optional(),
        offline: z.number().optional(),
        requested: z.number().optional(),
        resolved: z.number().optional(),
        unresolved: z.number().optional()
    }).optional(),
    callId: z.string().optional(),
    calleeId: z.string().optional(),
    calleeName: z.string().nullable().optional(),
    calleePhone: z.string().optional(),
    deliveryReason: z.string().optional(),
    // The callee's Do Not Disturb message, on a direct call only (Settings, ≤200 characters).
    dndMessage: z.string().max(200).nullable().optional(),
    // The callee's signed VideoMail greeting (voicemailFallbackForClient);
    // its URLs are presigned for 5 minutes.
    greeting: z.object({
        durationSeconds: z.number().nullable().optional(),
        id: z.string(),
        playbackUrl: z.string().nullable().optional(),
        thumbnailUrl: z.string().nullable().optional()
    }).passthrough().nullable().optional(),
    groupCall: z.boolean().optional(),
    participantCount: z.number().optional(),
    reason: z.string().optional(),
    // A group call: the entries that matched no account (as typed).
    unresolved: z.array(z.unknown()).optional(),
    voicemailAvailable: z.boolean().optional()
}).passthrough();
/*
 * Deaf-to-deaf group calls (2026-09-24). Any joined participant may ring
 * someone else in with conference_add; the server answers the inviter with
 * conference_invite_sent (or an error carrying operation 'conference_add')
 * and later conference_invite_result. Everyone in the call hears who joined
 * and who left; only a real call_ended ends the room.
 */
export const conferenceInviteSentSchema = z.object({
    callId: z.string().optional(),
    expiresAt: z.string().optional(),
    inviteId: z.string().optional(),
    // What was asked for, as sent: the handle, email, number or clientId.
    requested: z.string().nullable().optional(),
    // True when the server rang the person's devices.
    ringing: z.boolean().optional(),
    // Who it resolved to: { clientId, name }.
    target: z.unknown().optional()
}).passthrough();
/**
 * How an invitation's ring ended. 'cancelled': the inviter or the host
 * stopped it (conference_invite_cancel { callId, targetId }); answered by
 * this, or by error { code, operation: 'conference_invite_cancel', callId,
 * targetId } with NOT_INVITER, NOT_RINGING, NOT_PARTICIPANT, CALL_ENDED,
 * NOT_FOUND, AUTH_REQUIRED or INTERNAL_ERROR.
 */
export const conferenceInviteStatusSchema = z.enum(['accepted', 'declined', 'missed', 'cancelled']);
export const conferenceInviteResultSchema = z.object({
    callId: z.string().optional(),
    inviteId: z.string().optional(),
    name: z.string().nullable().optional(),
    status: conferenceInviteStatusSchema.optional(),
    targetId: z.string().optional()
}).passthrough();
export const p2pParticipantPresenceSchema = z.object({
    callId: z.string().optional(),
    name: z.string().optional(),
    participantCount: z.number().optional(),
    // joined: came back after leaving; left: taken out by another member.
    rejoined: z.boolean().optional(),
    removed: z.boolean().optional(),
    userId: z.string().optional(),
    // The group call's host after this change (it passes on when the host leaves).
    hostId: z.string().optional()
}).passthrough();
const callPeerListSchema = z.array(z.object({ id: z.string().optional(), name: z.string().nullable().optional() }).passthrough());
/**
 * Why call_ended closed a deaf-to-deaf group call from its host's hang-up
 * menu: 'ended_for_all' (p2p_end_for_all: everyone in it or ringing for it
 * hears it, with endedBy/endedByName) or 'already_ended' (the host's repeat
 * of an end that already happened). Other call_ended reasons are free text.
 */
export const groupCallEndReasonSchema = z.enum(['ended_for_all', 'already_ended']);
/** Nobody answered a group call the caller is waiting in (p2p_group_no_answer). */
export const p2pGroupNoAnswerSchema = z.object({
    callId: z.string().optional(),
    declined: callPeerListSchema.optional(),
    missed: callPeerListSchema.optional(),
    roomName: z.string().optional()
}).passthrough();
/**
 * A ring this client missed while the call is still live
 * (p2p_room_still_active). A group invitee may still join it by accepting
 * again: p2p_accept { callId, callerId, roomName }, answered by p2p_join_room.
 */
export const p2pRoomStillActiveSchema = z.object({
    callId: z.string().optional(),
    callerId: z.string().optional(),
    callerName: z.string().optional(),
    groupCall: z.boolean().optional(),
    participantCount: z.number().optional(),
    roomName: z.string().optional()
}).passthrough();
/**
 * An instant meeting started: a live call with only this client, whose room
 * they enter at once (instant_meeting_started, answering instant_meeting_start
 * with the same requestId; ws/p2p-handlers.js handleInstantMeetingStart).
 * Refusals arrive as `error` with operation 'instant_meeting_start'.
 */
export const instantMeetingStartedSchema = z.object({
    callId: z.string().min(1),
    clientId: z.string().min(1),
    requestId: z.string().optional(),
    roomName: z.string().min(1)
}).passthrough();
/**
 * A call ringing while this client is in another one
 * (p2p_incoming_call_waiting). Answered with call_waiting_respond
 * { incomingCallId, currentCallId, action: 'accept' | 'hold_and_accept' | 'reject' }.
 */
export const callWaitingIncomingSchema = p2pIncomingSchema.extend({
    currentCallId: z.string().optional(),
    // false: the current call has an interpreter, so no Hold & answer
    // (vrs-server/lib/call-hold-policy.js; Robert, 2026-10-12).
    holdAllowed: z.boolean().optional()
}).passthrough();
/** call_waiting_responded: the server's word on this device's answer. */
export const callWaitingRespondedSchema = z.object({
    action: z.string().optional(),
    callId: z.string().optional(),
    callerName: z.string().nullable().optional(),
    heldCallId: z.string().nullable().optional(),
    roomName: z.string().optional()
}).passthrough();
/*
 * The host decides who joins a group call again (vrs-server/ws/group-join-approval.js).
 */
/** group_join_request, to the host: someone asks to join (again). */
export const groupJoinRequestSchema = z.object({
    callId: z.string().min(1),
    requestId: z.string().min(1),
    userId: z.string().min(1),
    name: z.string().nullable().optional(),
    rejoin: z.boolean().optional(),
    timeoutMs: z.number().optional()
}).passthrough();
/** group_join_resolved, to the host's devices: the question ended. */
export const groupJoinResolvedSchema = z.object({
    callId: z.string().min(1),
    requestId: z.string().min(1),
    outcome: z.string()
}).passthrough();
/** group_join_waiting, to the person asking: the host is asked. */
export const groupJoinWaitingSchema = z.object({
    callId: z.string().min(1),
    requestId: z.string().min(1),
    timeoutMs: z.number().optional()
}).passthrough();
/**
 * call_merged: the call this person was in (on hold) was merged into another
 * one; they enter its room (vrs-server/ws/call-management-handlers.js
 * handleCallMerge).
 */
export const callMergedSchema = z.object({
    callId: z.string().min(1),
    fromCallId: z.string().min(1),
    groupCall: z.boolean().optional(),
    hostId: z.string().nullable().optional(),
    mergedBy: z.string().nullable().optional(),
    roomName: z.string().min(1)
}).passthrough();
/** held_call_status: whether the call this device kept on hold is still live and on hold. */
export const heldCallStatusSchema = z.object({
    callId: z.string().min(1),
    live: z.boolean()
}).passthrough();
/**
 * call_merge_done: the merger's own word that the held call joined this one.
 * hostId: the merger hosts the group call now (Robert, 2026-10-29).
 */
export const callMergeDoneSchema = z.object({
    callId: z.string().min(1),
    heldCallId: z.string().min(1),
    hostId: z.string().nullable().optional(),
    moved: z.number().optional()
}).passthrough();
/** call_on_hold / call_off_hold: someone else in this call held it, or is back. */
export const callHoldNoticeSchema = z.object({
    callId: z.string().optional(),
    heldBy: z.string().nullable().optional()
}).passthrough();
/**
 * A missed call not yet marked seen (GET /api/client/missed-calls, and the
 * missed_calls WebSocket message). roomStillActive: the call it belonged to
 * is still live, so it may be joined instead of called back.
 */
export const missedCallSchema = z.object({
    id: z.union([z.string(), z.number()]).transform(String),
    caller_id: z.string().nullable().optional(),
    caller_name: z.string().nullable().optional(),
    caller_phone: z.string().nullable().optional(),
    created_at: z.string(),
    seen: z.boolean().optional(),
    room_name: z.string().nullable().optional(),
    active_call_id: z.string().nullable().optional(),
    active_room_name: z.string().nullable().optional(),
    room_still_active: z.boolean().optional()
}).passthrough();
/**
 * One live call per account across devices (vrs-server/lib/call-holders.js).
 * A second device is never silently let into, or handed, a call another
 * device of the same account is carrying: it is told which device holds the
 * call (a display label such as "Chrome on Mac") and since when.
 * Interpreters wait (session_held_elsewhere); a client may move the call.
 */
export const accountCallHoldSchema = z.object({
    role: z.enum(['client', 'interpreter']).optional(),
    callId: z.string().optional(),
    roomName: z.string().nullable().optional(),
    device: z.string().optional(),
    since: z.string().nullable().optional(),
    canMoveHere: z.boolean().optional(),
    recheckAfterMs: z.number().optional(),
    message: z.string().optional()
}).passthrough();
export const accountCallStatusSchema = accountCallHoldSchema.extend({
    held: z.boolean()
}).passthrough();
/** call_moving_elsewhere / call_moved_elsewhere / call_move_lapsed. */
export const callMoveNoticeSchema = z.object({
    callId: z.string().optional(),
    roomName: z.string().nullable().optional(),
    device: z.string().optional(),
    message: z.string().optional()
}).passthrough();
/**
 * The reconnect grace (vrs-server/ws/call-reconnect-grace.js). Someone in
 * the call left without hanging up (page closed or reloading, connection
 * lost) and the call is held for them (call_peer_reconnecting), or they are
 * back (call_peer_reconnected). `anchor`: the person an interpreter is tied
 * to, whose not coming back ends the call for everyone. secondsLeft is
 * measured by the server's clock.
 */
export const peerReconnectingSchema = z.object({
    callId: z.string().optional(),
    roomName: z.string().nullable().optional(),
    userId: z.string().optional(),
    role: z.string().optional(),
    name: z.string().nullable().optional(),
    awaySince: z.string().optional(),
    graceEndsAt: z.string().optional(),
    secondsLeft: z.number().optional(),
    cause: z.string().optional(),
    anchor: z.boolean().optional()
}).passthrough();
export const peerReconnectedSchema = z.object({
    callId: z.string().optional(),
    roomName: z.string().nullable().optional(),
    userId: z.string().optional(),
    role: z.string().optional(),
    awayMs: z.number().optional()
}).passthrough();
/**
 * To the person who left: their call is held and they may take it back
 * (call_reconnect_available), or it no longer is because they are back
 * (call_reconnect_resolved). An ended call is call_ended as ever.
 */
export const callReconnectAvailableSchema = z.object({
    callId: z.string().optional(),
    roomName: z.string().nullable().optional(),
    role: z.string().optional(),
    callType: z.string().nullable().optional(),
    graceEndsAt: z.string().optional(),
    secondsLeft: z.number().optional()
}).passthrough();
export const callReconnectResolvedSchema = z.object({
    callId: z.string().optional(),
    reason: z.string().optional()
}).passthrough();
export const callEndIgnoredSchema = z.object({
    callId: z.string().optional(),
    reason: z.string().optional(),
    message: z.string().optional()
}).passthrough();
/**
 * Why request_withdrawn took an interpreter's offer card down (vrs-server
 * lib/queue-service.js, ws/queue-notify.js, ws/queue-handlers.js). The shared
 * queue service emits request_withdrawn as requestCancelled, so that event
 * carries these as well as request_cancelled's own reasons. 'queue_paused':
 * an admin paused the queue for the request's organization or the platform;
 * the request stays waiting and is offered again on resume, so it is neither
 * a decline nor an expiry. 'offer_released' and 'queue_paused' name the offer
 * generation they end (offerId).
 */
export const requestWithdrawnReasonSchema = z.enum([
    'assigned',
    'cancelled',
    'declined',
    'offer_expired',
    'offer_released',
    'queue_paused',
    'removed',
    // An admin's service-region change took it back (docs/regional-queue-routing.md).
    'service_region_revoked'
]);
export const queueEventSchemas = {
    authenticated: z.object({
        clientId: z.string().optional(),
        role: z.string().optional(),
        // Server-decided features (vrs-server/ws/auth-handlers.js).
        capabilities: z.object({
            calendarReminders: z.boolean().optional(),
            externalMeetingVrs: z.boolean().optional(),
            // How long a page closing mid-call is held for it to come back
            // (vrs-server/ws/call-reconnect-grace.js); 0: leaving ends it.
            reconnectGraceMs: z.number().optional(),
            // INTERPRETED_ANCHOR_RULE=enforce (docs/interpreted-group-calls.md).
            interpretedAnchor: z.boolean().optional(),
            // PHONE_CAPTIONS_ENABLED: the interpreter's phone call can be
            // captioned for the room (vrs-server src/lib/phone-caption-stream.ts).
            phoneCaptions: z.boolean().optional(),
            // Offers ring Malka Terp while it is off the screen
            // (vrs-server/ws/interpreter-standby.js).
            standbyRing: z.boolean().optional(),
            // This server has web console desks for outside meetings
            // (docs/outside-meeting-desk.md).
            meetingDesk: z.boolean().optional(),
            // The account may use watch alerts: the web "TV alerts" card and
            // the PineTime watch bridge in the app (docs/watch-alerts.md).
            watchAlerts: z.boolean().optional()
        }).passthrough().optional()
    }).passthrough(),
    callHoldUpdated: looseEventSchema,
    callInterpreterChanged: callInterpreterChangedPayloadSchema,
    callMergeDone: callMergeDoneSchema,
    heldCallStatus: heldCallStatusSchema,
    callMerged: callMergedSchema,
    callOffHold: callHoldNoticeSchema,
    callOnHold: callHoldNoticeSchema,
    callPhoneLegHandedOver: callPhoneLegHandedOverPayloadSchema,
    callTransferAccepted: looseEventSchema,
    callTransferCancelled: looseEventSchema,
    callTransferInitiated: looseEventSchema,
    callTransferOffer: callTransferOfferPayloadSchema,
    callTransferOfferEnded: callTransferOfferEndedPayloadSchema,
    teamOffer: teamOfferPayloadSchema,
    teamOfferEnded: teamOfferEndedPayloadSchema,
    teamJoined: teamJoinedPayloadSchema,
    teamRequestPending: teamStatePayloadSchema,
    teamAccepted: teamStatePayloadSchema,
    teamRequestEnded: teamStatePayloadSchema,
    teamEnded: teamStatePayloadSchema,
    callTransferPhoneLegFailed: callTransferPhoneLegFailedPayloadSchema,
    callTransferPending: looseEventSchema,
    callWaitingIncoming: callWaitingIncomingSchema,
    callWaitingResponded: callWaitingRespondedSchema,
    groupJoinRequest: groupJoinRequestSchema,
    groupJoinResolved: groupJoinResolvedSchema,
    groupJoinWaiting: groupJoinWaitingSchema,
    chatHistory: looseEventSchema,
    chatMessage: looseEventSchema,
    chatMessageSent: looseEventSchema,
    callEnded: queueMatchPayloadSchema,
    clientReringSent: looseEventSchema,
    clientReringSkipped: looseEventSchema,
    conferenceAddRinging: looseEventSchema,
    conferenceInvite: looseEventSchema,
    conferenceInviteResult: conferenceInviteResultSchema,
    conferenceInviteSent: conferenceInviteSentSchema,
    conferenceInvitationError: z.object({ invitationId: z.string().optional(), callId: z.string().optional(), code: z.string().optional(), message: z.string().optional() }),
    // 'expired': the ring is over without an answer (it ran out, its inviter
    // cancelled it, or the host ended the call for everyone).
    conferenceInvitationUpdate: z.object({ invitationId: z.string().optional(), callId: z.string().optional(), participantId: z.string().optional(), status: z.enum(['accepted', 'declined', 'unavailable', 'expired']).optional() }),
    conferenceParticipantRemoved: looseEventSchema,
    conferenceRemoved: looseEventSchema,
    connection: z.object({
        connected: z.boolean(),
        maxAttemptsReached: z.boolean().optional(),
        message: z.string().optional()
    }).passthrough(),
    contactsChanged: looseEventSchema,
    error: queueErrorPayloadSchema,
    handoff_complete: looseEventSchema,
    handoff_consumed: looseEventSchema,
    handoff_cancelled: looseEventSchema,
    handoff_error: looseEventSchema,
    handoff_executed: looseEventSchema,
    handoff_devices_updated: looseEventSchema,
    handoff_in_progress: looseEventSchema,
    handoff_offer: looseEventSchema,
    handoff_prepared: looseEventSchema,
    handoff_release: looseEventSchema,
    inboundVrsCall: looseEventSchema,
    interpreterRequest: interpreterRequestPayloadSchema,
    matchFound: queueMatchPayloadSchema,
    meetingInitiated: queueMatchPayloadSchema,
    // Calendar meetings that are starting (phase 2); ids and platforms, never links.
    meetingReminder: z.object({ reminders: z.array(z.unknown()).optional() }).passthrough(),
    // The interpreter entered / identified the client in their own meeting.
    externalMeetingProgress: z.object({
        callId: z.string().optional(),
        event: z.enum(['entered', 'identified']).optional(),
        platform: z.string().nullable().optional()
    }).passthrough(),
    // The interpreter's side of an outside meeting (docs/outside-meeting-desk.md).
    meetingDeskStatus: meetingDeskStatusPayloadSchema,
    // A TV alert relayed from this account's web Home: the app vibrates the
    // paired PineTime watch (docs/watch-alerts.md).
    watchAlert: watchAlertPayloadSchema,
    externalMeetingCall: externalMeetingCallPayloadSchema,
    externalMeetingStatusAck: externalMeetingStatusAckPayloadSchema,
    notificationEvent: notificationEventPayloadSchema,
    p2pCallFailed: p2pFailureSchema,
    p2pIncomingCall: p2pIncomingSchema,
    p2pAccepted: p2pIncomingSchema,
    p2pCallCancelled: looseEventSchema,
    p2pCallEnded: p2pIncomingSchema,
    p2pDeclined: p2pIncomingSchema,
    // Nobody answered a group call the caller is still waiting on.
    p2pGroupNoAnswer: p2pGroupNoAnswerSchema,
    p2pJoinRoom: p2pIncomingSchema,
    // A ring on this person's devices is over (vrs-server/ws/ring-ledger.js,
    // docs/push-ring-lifecycle.md). Sent to every socket of the person.
    ringEnded: ringEndedSchema,
    p2pParticipantJoined: p2pParticipantPresenceSchema,
    p2pParticipantLeft: p2pParticipantPresenceSchema,
    // In-call "call an interpreter into this P2P room" (green button).
    p2pInterpreterRequested: z.object({
        callId: z.string(),
        requestId: z.string().optional()
    }).passthrough(),
    // Captions of the interpreter's phone call (vrs-server
    // ws/phone-caption-handlers.js); the interpreter's room shows them to all.
    phoneCaption: z.object({
        callId: z.string(),
        confidence: z.number().nullable().optional(),
        isFinal: z.boolean(),
        segmentId: z.string(),
        text: z.string(),
        track: z.enum(['hearing', 'interpreter', 'deaf'])
    }).passthrough(),
    phoneCaptionsState: z.object({
        callId: z.string().optional(),
        code: z.string().optional(),
        running: z.boolean()
    }).passthrough(),
    p2pInterpreterJoined: z.object({
        callId: z.string().optional(),
        interpreterName: z.string().optional(),
        roomName: z.string().optional()
    }).passthrough(),
    // The anchor of an interpreted group call left and the call goes on: the
    // interpreter now serves, and bills, the next Deaf person (Robert, 2026-10-05).
    p2pAnchorChanged: z.object({
        anchorId: z.string().optional(),
        anchorName: z.string().nullable().optional(),
        callId: z.string().optional(),
        previousAnchorId: z.string().optional(),
        roomName: z.string().optional()
    }).passthrough(),
    p2pInterpreterUnavailable: z.object({
        callId: z.string().optional(),
        // A revoked region grant withdrew the request: SERVICE_REGION_REVOKED.
        code: z.string().optional(),
        message: z.string().optional(),
        reason: z.string().optional()
    }).passthrough(),
    // An admin changed where this account may be served; null = not designated
    // (docs/regional-queue-routing.md#grants-and-revocation).
    serviceRegionsUpdated: z.object({
        serviceRegions: z.array(z.string()).nullable()
    }),
    // Interpreted calls tied to the person who asked for the interpreter
    // (docs/interpreted-group-calls.md).
    p2pInterpreterLeft: z.object({
        callId: z.string().optional(),
        interpreterId: z.string().optional(),
        interpreterName: z.string().nullable().optional(),
        roomName: z.string().optional()
    }).passthrough(),
    anchorHold: z.object({
        anchorId: z.string().optional(),
        anchorName: z.string().nullable().optional(),
        anchorState: z.string().optional(),
        callId: z.string().optional(),
        expiresAt: z.string().nullable().optional(),
        interpreterName: z.string().nullable().optional(),
        roomName: z.string().optional()
    }).passthrough(),
    anchorHoldUpdate: z.object({ anchorState: z.string().optional(), callId: z.string().optional() }).passthrough(),
    anchorHoldReleased: z.object({
        callId: z.string().optional(),
        expiresInSeconds: z.number().optional(),
        roomName: z.string().optional(),
        roomToken: z.string().optional()
    }).passthrough(),
    anchorHoldEnded: z.object({ callId: z.string().optional(), reason: z.string().optional() }).passthrough(),
    interpreterCallbackStatus: z.object({
        attempt: z.number().nullable().optional(),
        callbackCallId: z.string().optional(),
        closeReason: z.string().optional(),
        members: z.array(z.object({
            id: z.string().optional(),
            name: z.string().nullable().optional(),
            role: z.string().optional(),
            state: z.string().optional()
        }).passthrough()).optional(),
        parentCallId: z.string().nullable().optional(),
        state: z.string().optional()
    }).passthrough(),
    p2pRinging: queueMatchPayloadSchema.extend({
        calleeId: z.string().optional(),
        calleeName: z.string().optional(),
        // A direct call: the photo of the person called (a signed link).
        calleePhotoUrl: z.string().optional(),
        // 'socket' — the callee's app is open; 'push' — their device was
        // woken; 'unconfirmed' — ringing on hope (no device could be
        // alerted) while the invite waits for them to connect.
        delivery: z.enum(['socket', 'push', 'unconfirmed']).optional(),
        deliveryReason: z.string().optional(),
        groupCall: z.boolean().optional(),
        participantCount: z.number().optional(),
        // A group call's first round: who is being rung, who could not be
        // alerted yet (still rung, invite held for reconnect), and who has
        // Do Not Disturb on (not rung, no seat).
        invited: callPeerListSchema.optional(),
        offline: callPeerListSchema.optional(),
        dnd: callPeerListSchema.optional(),
        // Group invitees the server could not reach, as entered (unknown,
        // private or blocked handles look alike on purpose).
        unresolved: z.array(z.unknown()).optional()
    }).passthrough(),
    p2pRoomStillActive: p2pRoomStillActiveSchema,
    instantMeetingStarted: instantMeetingStartedSchema,
    // Missed calls not yet seen (missed_calls): the whole unseen list.
    missedCalls: z.object({ calls: z.array(missedCallSchema) }).passthrough(),
    p2pTargetDnd: p2pTargetSchema,
    p2pTargetOffline: p2pTargetSchema,
    vrsClientUnavailable: p2pTargetSchema,
    vrsClientReconnecting: vrsClientReachabilitySchema,
    p2p_target_offline: p2pTargetSchema,
    preferencesUpdated: looseEventSchema,
    queueStatus: queueStatusSchema,
    requestAccepted: queueMatchPayloadSchema,
    requestAssigned: queueMatchPayloadSchema,
    requestCancelled: z.object({
        /** The offer generation a withdrawal ends; a card showing a newer one keeps it. */
        offerId: z.string().optional(),
        reason: z.union([z.enum(['timeout', 'removed_by_admin']), requestWithdrawnReasonSchema]).optional(),
        /** SERVICE_REGION_REVOKED with its message when a revoked region grant withdrew it. */
        code: z.string().optional(),
        message: z.string().optional(),
        requestId: z.string().optional(),
        /** A scheduled request was cancelled (it was never in the live queue). */
        scheduled: z.boolean().optional(),
        success: z.boolean().optional(),
        timedOut: z.boolean().optional()
    }).passthrough(),
    requestDeclined: queueMatchPayloadSchema,
    // An interpreter request stored for later (request_interpreter with
    // schedule: true). reused: the account's existing scheduled request.
    requestScheduled: z.object({
        callType: z.string().optional(),
        requestId: z.string().optional(),
        reused: z.boolean().optional(),
        scheduledStartAt: z.string().nullable().optional()
    }).passthrough(),
    requestQueued: z.object({
        position: z.number().optional(),
        requestId: z.string().optional(),
        /** Epoch ms (server clock) the request was created; a reused request keeps its time. */
        queuedAt: z.number().optional()
    }).passthrough(),
    session_registered: looseEventSchema,
    session_unregistered: looseEventSchema,
    adminForceLogout: z.object({ reason: z.string().optional(), message: z.string().optional() }).passthrough(),
    sessionSuperseded: z.object({ code: z.string().optional(), message: z.string().optional() }).passthrough(),
    // One live call per account: this device is held out / the call is on
    // (or moving to, or moved to) another device of the same account.
    sessionHeldElsewhere: accountCallHoldSchema,
    accountCallStatus: accountCallStatusSchema,
    callActiveElsewhere: accountCallHoldSchema,
    callMovingElsewhere: callMoveNoticeSchema,
    callMovedElsewhere: callMoveNoticeSchema,
    callMoveLapsed: callMoveNoticeSchema,
    callEndIgnored: callEndIgnoredSchema,
    peerReconnecting: peerReconnectingSchema,
    peerReconnected: peerReconnectedSchema,
    callReconnectAvailable: callReconnectAvailableSchema,
    callReconnectResolved: callReconnectResolvedSchema,
    // The delayed half of a kick: the server already destroyed the session, so
    // the next reconnect is rejected with SESSION_REVOKED and refresh cannot
    // recover it. Distinct from adminForceLogout, which arrives live.
    interpreterClientCallFailed: interpreterClientCallFailedPayloadSchema,
    interpreterClientCallStarted: interpreterClientCallStartedPayloadSchema,
    sessionRevoked: z.object({ code: z.string().optional(), message: z.string().optional() }).passthrough(),
    // "Sign out of all apps" (or a sign-out in another app of the sign-in)
    // ended this session: the live signed_out message (vrs-server/lib/sign-out-all.js).
    signedOut: z.object({}).passthrough(),
    statusUpdated: z.object({ status: z.string().optional() }).passthrough(),
    voicemail_error: voicemailEventPayloadSchema,
    voicemail_message_deleted: voicemailEventPayloadSchema,
    voicemail_new_message: voicemailEventPayloadSchema,
    voicemail_recording_cancelled: voicemailEventPayloadSchema,
    voicemail_recording_complete: voicemailEventPayloadSchema,
    voicemail_recording_started: voicemailEventPayloadSchema,
    voicemail_unread_count: voicemailEventPayloadSchema,
    vriInvitePrepared: vriInvitePreparedPayloadSchema
};
export const queueSequenceContracts = {
    requestInterpreter: [
        'connection',
        'requestQueued',
        'interpreterRequest',
        'requestAccepted',
        'matchFound',
        'meetingInitiated'
    ],
    interpreterAvailability: [
        'connection',
        'authenticated',
        'statusUpdated',
        'queueStatus'
    ],
    vriInvitePreparation: [
        'connection',
        'vriInvitePrepared'
    ],
    interpreterCallsClient: [
        'connection',
        'authenticated',
        'interpreterClientCallStarted',
        'meetingInitiated'
    ]
};
export const queueEventNames = Object.keys(queueEventSchemas);
